/**
 * Custom hook that encapsulates a single paginated section of the Devices page.
 *
 * Uses the dedicated `rescueDevices` query which:
 *  - Only returns device-bearing pets (accurate totalCount)
 *  - Uses offset/limit pagination
 *  - Includes transfer details inline (no N+1 enrichment)
 *
 * Transition strategy
 * ───────────────────
 * Apollo's `previousData` keeps the last successful result available when
 * variables change.  We use `data ?? previousData` so the table always has
 * rows to render — old rows stay visible (dimmed via the overlay CSS) while
 * the network request for the new page / search is in flight.
 *
 * `skeletonCount > 0` tells the table component to show loading UI:
 *   • If stale rows exist  →  dim overlay + shimmer  (height stays the same)
 *   • If no rows at all    →  skeleton placeholder rows  (initial load only)
 */

import { useState, useMemo, useCallback, useRef } from 'react';
import { useQuery } from '@apollo/client';
import type { Device, RescueDeviceSortColumn, SortDir } from '../lib/types';
import type { RescueDevicesData, RescueDeviceFilter } from '../lib/rescueQueries';
import { rescueDevicesPagedQuery } from '../lib/rescueQueries';
import { mapRescueDeviceItemToDevice } from '../lib/deviceAdapters';
import useStaleCut from './useStaleCut';

/**
 * The column each sortable header stands for, in the schema's spelling. The keys are the
 * page's own vocabulary (`placed` is the Placed On column); `RescueDeviceSortKey` is the
 * server's. Kept as a total map so adding a column without a server key fails to compile.
 *
 * This mirrors what the schema can order by, not what the page offers: `holder` is here
 * because the server accepts it, and absent from {@link DeviceSortKey} — the subset the
 * Activated and Fosters headers sort on — because it orders by the wrong name. The reason
 * lives with that type.
 */
const SORT_KEY_TO_GQL: Record<RescueDeviceSortColumn, string> = {
  pet: 'PET',
  holder: 'HOLDER',
  placed: 'PLACED_ON',
  device: 'DEVICE',
  adopter: 'ADOPTER',
  transferred: 'TRANSFERRED',
  stalled: 'STALLED',
  lastTextReminder: 'LAST_TEXT_REMINDER',
};

// ── Hook ────────────────────────────────────

export interface UseRescueDevicesResult {
  /** Device-bearing pets on the current page. */
  devices: Device[];
  /** Accurate count of ALL devices matching this filter (from server). */
  totalCount: number;
  /** Whether there are more pages after the current one. */
  hasMore: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  /**
   * Number of skeleton placeholder rows to show while loading.
   * Based on the previous page's item count so the table maintains its height.
   * Falls back to pageSize on initial load.
   */
  skeletonCount: number;
  error: Error | undefined;
  /** Re-runs this section's query — the retry behind its error row. */
  refetch: () => void;
  goNextPage: () => void;
  goPrevPage: () => void;
  goToPage: (page: number) => void;
  resetPage: () => void;
}

/**
 * `searchQuery` is expected to arrive already debounced — the caller owns the delay, since it
 * also drives the un-debounced input value and the `?search=` URL param. Debouncing again in
 * here would stagger this query behind the other queries on the same page, which read the same
 * value directly: the two would answer for different searches for the length of the extra
 * delay, and a section rendering rows from one under a count from the other disagrees with
 * itself on screen.
 */
export default function useRescueDevices(
  filter: RescueDeviceFilter,
  searchQuery: string,
  pageSize: number,
  resellerName: string,
  completedSince?: string,
  options?: { skip?: boolean; sortKey?: RescueDeviceSortColumn | null; sortDir?: SortDir; stalled?: boolean },
): UseRescueDevicesResult {
  const skip = options?.skip ?? false;
  const sortKey = options?.sortKey ?? null;
  const sortDir = options?.sortDir;
  const stalled = options?.stalled;
  // ── Pagination state ──────────────────────

  const [page, setPage] = useState(1);

  // Reset pagination when the search or the sort changes. Both re-order the whole result set,
  // so the page the user is on indexes into a different list than the one they were reading —
  // page 3 of the old order is a mid-list slice of the new one.
  //
  // Adjusted during render rather than in an effect, and read through a local the rest of this
  // render uses. An effect lands a render too late: the variables built below would still carry
  // the old offset under the new ordering, so clicking a column header from page 3 fires a
  // request for a page nobody will see before the real one goes out.
  const signature = `${searchQuery}|${sortKey}|${sortDir}`;
  const [prevSignature, setPrevSignature] = useState(signature);
  let currentPage = page;
  if (prevSignature !== signature) {
    setPrevSignature(signature);
    setPage(1);
    currentPage = 1;
  }

  // ── Build query variables ─────────────────

  const offset = (currentPage - 1) * pageSize;

  const variables = useMemo(() => {
    const input: Record<string, unknown> = {
      filter,
      limit: pageSize,
      offset,
    };
    if (searchQuery) {
      input.query = searchQuery;
    }
    if (completedSince) {
      input.completedSince = completedSince;
    }
    // Omitted entirely when no column is active, which is what asks the server for its own
    // default ordering — sending a null `sortKey` is not the same request.
    if (sortKey) {
      input.sortKey = SORT_KEY_TO_GQL[sortKey];
      input.sortDirection = sortDir === 'desc' ? 'DESC' : 'ASC';
    }
    // Narrows PENDING_ADOPTIONS to one side of the fresh/stalled split. Omitted — not sent as
    // null — asks for the union, which is what `rescueDeviceStats` counts.
    if (stalled !== undefined) {
      input.stalled = stalled;
    }
    return { input };
  }, [filter, pageSize, offset, searchQuery, completedSince, sortKey, sortDir, stalled]);

  // ── GraphQL query ─────────────────────────

  const { data, previousData, loading, error, refetch } = useQuery<RescueDevicesData>(rescueDevicesPagedQuery, {
    variables,
    fetchPolicy: 'cache-and-network',
    skip,
  });

  // ── Keep stale rows visible during transitions ──────────
  //
  // When variables change (cache miss), `data` becomes undefined while the
  // network request is in flight.  `previousData` holds the result from the
  // last *successful* query, so `data ?? previousData` always has rows to
  // display — the table never flashes empty.
  //
  // Unless the query failed for a cut it has never answered: those rows belong to the search
  // or sort before this one, and the section's error row yields to rows, so keeping them would
  // present the old list as the new answer and swallow the failure with it. See useStaleCut.

  const stale = useStaleCut(JSON.stringify(variables), !!data);
  const effectiveData = stale && error ? undefined : (data ?? previousData);
  const result = effectiveData?.rescueDevices;
  const totalCount = result?.totalCount ?? 0;
  const hasMore = result?.hasMore ?? false;

  // ── Map items to Device view-models ───────

  const devices: Device[] = useMemo(() => {
    if (!result?.items) return [];
    return result.items
      .map((item) => mapRescueDeviceItemToDevice(item, resellerName, filter))
      .filter((d): d is Device => d !== null);
  }, [result?.items, resellerName, filter]);

  // ── Skeleton / transition state ───────────────────────────────
  //
  // `skeletonCount > 0` signals the table to show loading UI.
  // The table decides which mode based on whether it has visible rows:
  //   • rows present  → dim overlay with shimmer (preserves table height)
  //   • no rows       → skeleton placeholder bars
  //
  // We are "transitioning" when Apollo is loading and current-variable data
  // hasn't arrived yet (we're showing previousData as a fallback).

  const prevItemCountRef = useRef(pageSize);
  if (devices.length > 0) {
    prevItemCountRef.current = devices.length;
  }

  const isTransitioning = loading && !data;
  const skeletonCount = isTransitioning ? prevItemCountRef.current : 0;

  // ── Computed pagination state ─────────────

  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = hasMore;
  const hasPreviousPage = currentPage > 1;

  // ── Navigation handlers ───────────────────

  const goNextPage = useCallback(() => {
    if (hasMore) {
      setPage((p) => p + 1);
    }
  }, [hasMore]);

  const goPrevPage = useCallback(() => {
    setPage((p) => Math.max(1, p - 1));
  }, []);

  const goToPage = useCallback((target: number) => {
    setPage(Math.max(1, target));
  }, []);

  const resetPage = useCallback(() => {
    setPage(1);
  }, []);

  return {
    devices,
    totalCount,
    hasMore,
    hasNextPage,
    hasPreviousPage,
    currentPage,
    totalPages,
    loading,
    skeletonCount,
    error: error as Error | undefined,
    refetch: () => {
      // The failure is already on `error`, which re-renders the section's retry row. Letting
      // the rejection escape would report the same thing a second time, as an uncaught one.
      refetch().catch(() => {});
    },
    goNextPage,
    goPrevPage,
    goToPage,
    resetPage,
  };
}
